// src/pages/hr/recruitment/InterviewScheduler.jsx
import React, { useState } from "react";
import { useRecruitmentContext } from "../../../context/RecruitmentContext";
import { createInterview, submitInterviewFeedback } from "../../../hooks/useRecruitment";
import { useAuth } from "../../../context/AuthContext";

export default function InterviewScheduler() {
  const { interviews, interviewsLoading, candidates } = useRecruitmentContext();
  const { userProfile } = useAuth();

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);

  const [scheduleData, setScheduleData] = useState({
    candidateId: "",
    round: "Technical Round 1",
    interviewerName: "",
    scheduledDate: new Date().toISOString().split("T")[0],
    scheduledTime: "10:00",
    meetingLink: "",
    notes: "",
  });

  const [feedbackData, setFeedbackData] = useState({
    rating: 4,
    recommendation: "Hire",
    notes: "Strong technical fundamentals and good communication skills.",
  });

  async function handleScheduleSubmit(e) {
    e.preventDefault();
    const cand = candidates.find((c) => c.id === scheduleData.candidateId);
    await createInterview({
      ...scheduleData,
      candidateName: cand?.name || "Candidate",
      jobTitle: cand?.appliedPosition || "",
      createdByName: userProfile?.displayName || "HR",
    });
    setShowScheduleModal(false);
  }

  async function handleFeedbackSubmit(e) {
    e.preventDefault();
    if (!selectedInterview) return;
    await submitInterviewFeedback(selectedInterview.id, feedbackData);
    setShowFeedbackModal(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .int-table { width: 100%; border-collapse: collapse; font-size: 0.84rem; background: var(--bg-card, #fff); border-radius: 12px; overflow: hidden; border: 1px solid var(--border, #e5e7eb); box-shadow: 0 1px 5px rgba(0,0,0,0.05); }
        .int-table th { background: var(--bg-secondary, #f9fafb); text-align: left; padding: 0.7rem 0.9rem; font-size: 0.74rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid var(--border, #e5e7eb); }
        .int-table td { padding: 0.75rem 0.9rem; border-bottom: 1px solid var(--border, #f3f4f6); vertical-align: middle; color: var(--text-primary, #111); }
      `}</style>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h3 style={{ margin: 0, fontWeight: 700, fontSize: "1.05rem" }}>Interview Schedules & Feedback</h3>
          <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{interviews.length} total scheduled interviews</span>
        </div>
        <button className="btn-primary" onClick={() => setShowScheduleModal(true)}>+ Schedule Interview</button>
      </div>

      {interviewsLoading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>Loading Interviews...</div>
      ) : interviews.length === 0 ? (
        <div style={{ background: "var(--bg-card, #fff)", border: "1px dashed var(--border)", borderRadius: 12, padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
          No interviews scheduled yet. Click "+ Schedule Interview" to add one.
        </div>
      ) : (
        <table className="int-table">
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Round</th>
              <th>Interviewer</th>
              <th>Date & Time</th>
              <th>Meeting Link</th>
              <th>Status</th>
              <th>Feedback</th>
            </tr>
          </thead>
          <tbody>
            {interviews.map((item) => (
              <tr key={item.id}>
                <td style={{ fontWeight: 700 }}>{item.candidateName}</td>
                <td>
                  <span style={{ background: "var(--bg-secondary)", padding: "0.15rem 0.5rem", borderRadius: 4, fontSize: "0.74rem", fontWeight: 600 }}>
                    {item.round}
                  </span>
                </td>
                <td>{item.interviewerName || "—"}</td>
                <td>
                  <div style={{ fontWeight: 600 }}>{item.scheduledDate}</div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>{item.scheduledTime}</div>
                </td>
                <td>
                  {item.meetingLink ? (
                    <a href={item.meetingLink} target="_blank" rel="noreferrer" style={{ color: "#0ea5e9", textDecoration: "underline", fontSize: "0.78rem" }}>
                      Join Meeting
                    </a>
                  ) : "—"}
                </td>
                <td>
                  <span style={{
                    padding: "0.2rem 0.6rem", borderRadius: 99, fontSize: "0.72rem", fontWeight: 700,
                    background: item.status === "Completed" ? "#d1fae5" : "#fef3c7",
                    color: item.status === "Completed" ? "#065f46" : "#92400e"
                  }}>
                    {item.status || "Scheduled"}
                  </span>
                </td>
                <td>
                  {item.feedback ? (
                    <span style={{ fontSize: "0.75rem", color: "#10b981", fontWeight: 600 }}>
                      ⭐ {item.feedback.rating}/5 · {item.feedback.recommendation}
                    </span>
                  ) : (
                    <button
                      className="btn-secondary"
                      style={{ padding: "0.25rem 0.5rem", fontSize: "0.72rem" }}
                      onClick={() => {
                        setSelectedInterview(item);
                        setShowFeedbackModal(true);
                      }}
                    >
                      + Add Feedback
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="modal-overlay">
          <div className="modal-body" style={{ maxWidth: 520 }}>
            <h3 style={{ margin: "0 0 1rem", fontWeight: 700 }}>Schedule Candidate Interview</h3>
            <form onSubmit={handleScheduleSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Select Candidate *</label>
                <select
                  className="ats-select"
                  style={{ width: "100%" }}
                  required
                  value={scheduleData.candidateId}
                  onChange={(e) => setScheduleData({ ...scheduleData, candidateId: e.target.value })}
                >
                  <option value="">-- Choose Candidate --</option>
                  {candidates.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} ({c.appliedPosition || "Applicant"})</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Interview Round</label>
                <select
                  className="ats-select"
                  style={{ width: "100%" }}
                  value={scheduleData.round}
                  onChange={(e) => setScheduleData({ ...scheduleData, round: e.target.value })}
                >
                  <option value="Screening Round">Screening Round</option>
                  <option value="Technical Round 1">Technical Round 1</option>
                  <option value="Technical Round 2">Technical Round 2</option>
                  <option value="Managerial Round">Managerial Round</option>
                  <option value="HR Round">HR Round</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Interviewer Name</label>
                <input
                  type="text"
                  className="ats-input"
                  style={{ width: "100%" }}
                  placeholder="e.g. Lead Developer Name"
                  value={scheduleData.interviewerName}
                  onChange={(e) => setScheduleData({ ...scheduleData, interviewerName: e.target.value })}
                />
              </div>

              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Date *</label>
                  <input
                    type="date"
                    className="ats-input"
                    style={{ width: "100%" }}
                    required
                    value={scheduleData.scheduledDate}
                    onChange={(e) => setScheduleData({ ...scheduleData, scheduledDate: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Time *</label>
                  <input
                    type="time"
                    className="ats-input"
                    style={{ width: "100%" }}
                    required
                    value={scheduleData.scheduledTime}
                    onChange={(e) => setScheduleData({ ...scheduleData, scheduledTime: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Meeting Link (Google Meet / Teams)</label>
                <input
                  type="url"
                  className="ats-input"
                  style={{ width: "100%" }}
                  placeholder="https://meet.google.com/xyz-abc-123"
                  value={scheduleData.meetingLink}
                  onChange={(e) => setScheduleData({ ...scheduleData, meetingLink: e.target.value })}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                <button type="button" className="btn-secondary" onClick={() => setShowScheduleModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Schedule Interview</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="modal-overlay">
          <div className="modal-body" style={{ maxWidth: 500 }}>
            <h3 style={{ margin: "0 0 1rem", fontWeight: 700 }}>Submit Interview Feedback</h3>
            <form onSubmit={handleFeedbackSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Rating (1-5)</label>
                <select
                  className="ats-select"
                  style={{ width: "100%" }}
                  value={feedbackData.rating}
                  onChange={(e) => setFeedbackData({ ...feedbackData, rating: Number(e.target.value) })}
                >
                  <option value={5}>5 - Excellent Fit</option>
                  <option value={4}>4 - Strong Hire</option>
                  <option value={3}>3 - Acceptable</option>
                  <option value={2}>2 - Weak Fit</option>
                  <option value={1}>1 - Reject</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Recommendation</label>
                <select
                  className="ats-select"
                  style={{ width: "100%" }}
                  value={feedbackData.recommendation}
                  onChange={(e) => setFeedbackData({ ...feedbackData, recommendation: e.target.value })}
                >
                  <option value="Hire">Recommend Hire</option>
                  <option value="Hold">Hold / Re-interview</option>
                  <option value="Reject">Reject Candidate</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Detailed Feedback Notes</label>
                <textarea
                  className="ats-input"
                  style={{ width: "100%", height: 90, resize: "vertical" }}
                  value={feedbackData.notes}
                  onChange={(e) => setFeedbackData({ ...feedbackData, notes: e.target.value })}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                <button type="button" className="btn-secondary" onClick={() => setShowFeedbackModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Feedback</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
